import React, { useMemo } from "react";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import {
  Box,
  IconButton,
  MenuItem,
  Select as MuiSelect,
  Stack,
  styled,
  TableCell,
  TableRow,
  Typography
} from "@mui/material";
import { RoleComponentGuard } from "components/guards";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { TooltipDiv } from "components/tooltips/TooltipDiv";
import { useNavigate } from "hooks";
import { useFormatDateHook } from "hooks/shared-hooks/use-format-date-hook";
import { useReportsMetaSelector, useUserRoleSelector } from "store";
import { ISecurityTableRow } from "store/types/deal/securities";

import { RoleName } from "../../../constants";

const BodyCell = styled(TableCell)({
  fontSize: "14px",
  color: "#071F35",
  borderBottom: "1px solid #EDF2F7",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 0, // This forces the cell to respect max-width and enable text truncation
  padding: "6px 8px"
});

const Select = styled(MuiSelect)({
  width: "100%",
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none"
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none"
  },
  // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
  //   border: "1px solid #26A6AE"
  // },
  "& .MuiOutlinedInput-input.MuiInputBase-input": {
    padding: "4px 8px",
    fontSize: "14px",
    lineHeight: "16px",
    minHeight: 0,
    height: "auto",
    color: "#26A6AE",
    cursor: "pointer"
  },
  "& .MuiSelect-select": {
    padding: "4px 8px"
  },
  "& .MuiSelect-icon": {
    display: "none"
  }
});

// function formatCurrency(value: number): string {
//   return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
// }

interface SecuritiesTableRowProps {
  row: ISecurityTableRow;
  depth: number;
  expanded: Record<string, boolean>;
  onToggle: (id: number) => void;
  onSecurityClick: (row: ISecurityTableRow) => void;
  onAssignSecurity: (e: React.MouseEvent<HTMLButtonElement>, row: ISecurityTableRow) => void;
  onUndoSecurity: (row: ISecurityTableRow) => void;
  onAssetClassChange: (dealId: number, assetClassId: number) => Promise<void>;
}

export const SecuritiesTableRow: React.FC<SecuritiesTableRowProps> = ({
  row,
  depth,
  expanded,
  onToggle,
  onSecurityClick,
  onAssignSecurity,
  onUndoSecurity,
  onAssetClassChange
}) => {
  const role = useUserRoleSelector();
  const hasChildren = Boolean(row.children?.length);
  const isExpanded = expanded[row.deal_id] ?? false;
  const leftPadding = depth * 16;
  const isParent = !depth;
  const formatDate = useFormatDateHook();
  const { assetClasses } = useReportsMetaSelector();
  const parentAssetClasses = useMemo(
    () => assetClasses.filter((ac) => ac.parent_id == null),
    [assetClasses]
  );
  const navigate = useNavigate();

  const userHasEditPermission = useMemo(() => {
    return role?.name === RoleName.Admin || role?.name === RoleName.Analyst;
  }, [role]);

  return (
    <>
      <TableRow
        hover
        sx={{
          "& td": {
            ...(isParent && { fontWeight: 500 })
          },
          "& .actions-button": {
            opacity: 0,
            transition: "opacity 0.2s ease-in-out"
          },
          "&:hover .actions-button": {
            opacity: 1
          }
        }}
      >
        <BodyCell sx={{ fontWeight: isParent ? 500 : 400, maxWidth: 150 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {hasChildren ? (
              <Box component="span" onClick={() => onToggle(row.deal_id)} sx={{ cursor: "pointer", display: "flex" }}>
                {isExpanded ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                ) : (
                  <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                )}
              </Box>
            ) : (
              <Box sx={{ width: 18, minWidth: 18 }} />
            )}
            {isParent && (
              <MaterialTooltip title={row.deal_name}>
                <Typography
                  variant="body2"
                  onClick={() => navigate({ route: "dealWorkspace", params: { deal_id: row.deal_id } })}
                  sx={{
                    fontWeight: isParent ? 500 : 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "#26A6AE",
                    cursor: "pointer"
                  }}
                >
                  {row.deal_name}
                </Typography>
              </MaterialTooltip>
            )}
          </Stack>
        </BodyCell>
        <BodyCell sx={{ maxWidth: 150 }}>
          <MaterialTooltip title={row.security_name}>
            <Typography
              variant="body2"
              onClick={() => onSecurityClick(row)}
              sx={{
                color: "#26A6AE",
                cursor: "pointer",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {row.security_name ?? "-"}
            </Typography>
          </MaterialTooltip>
        </BodyCell>
        <BodyCell
          sx={{
            maxWidth: 150,
            ...(isParent && {
              "&:hover .edit-icon": {
                opacity: 1
              }
            })
          }}
        >
          {isParent && userHasEditPermission ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": {
                  backgroundColor: "#f5f7f7",
                  borderRadius: "4px"
                },
                padding: "4px 0px",
                margin: "-4px -8px"
              }}
            >
              <Select
                sx={{
                  maxWidth: "120px"
                }}
                value={row.asset_class_id || ""}
                onChange={(e) => onAssetClassChange(row.deal_id, e.target.value as number)}
                size="small"
              >
                {parentAssetClasses.map((assetClass) => (
                  <MenuItem key={assetClass.id} value={assetClass.id}>
                    {assetClass.title}
                  </MenuItem>
                ))}
              </Select>
              <EditOutlinedIcon className="edit-icon" sx={{ fontSize: 16, color: "#173C4C", opacity: 0 }} />
            </Box>
          ) : (
            <MaterialTooltip title={row.asset_class}>
              <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.asset_class}
              </Typography>
            </MaterialTooltip>
          )}
        </BodyCell>
        <BodyCell sx={{ maxWidth: 150 }}>
          <MaterialTooltip title={row.isin}>
            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.isin}
            </Typography>
          </MaterialTooltip>
        </BodyCell>
        <BodyCell sx={{ maxWidth: 150 }}>
          <MaterialTooltip title={row.investor_name}>
            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.investor_name}
            </Typography>
          </MaterialTooltip>
        </BodyCell>
        <BodyCell sx={{ maxWidth: 150 }}>
          <MaterialTooltip title={row.institution_name}>
            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.institution_name ?? "-"}
            </Typography>
          </MaterialTooltip>
        </BodyCell>
        <BodyCell sx={{ maxWidth: 120 }}>
          <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.account_number ?? "-"}
          </Typography>
        </BodyCell>
        <BodyCell sx={{ maxWidth: 120 }}>
          <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.branch_number ?? "-"}
          </Typography>
        </BodyCell>
        <BodyCell sx={{ maxWidth: 100 }}>
          <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.units}
          </Typography>
        </BodyCell>
        <BodyCell sx={{ maxWidth: 150 }}>{row.market_price || "N/A"}</BodyCell>
        <BodyCell sx={{ maxWidth: 150 }}>{row.nav}</BodyCell>
        <BodyCell sx={{ maxWidth: 120 }}>{row.profit ?? "-"}</BodyCell>
        <BodyCell sx={{ maxWidth: 120 }}>{row.twr ?? "-"}</BodyCell>
        <BodyCell sx={{ maxWidth: 120 }}>{row.initial_date ? formatDate({ date: row.initial_date }) : "-"}</BodyCell>
        <BodyCell sx={{ maxWidth: 120 }}>
          {row.effective_date ? formatDate({ date: row.effective_date }) : "-"}
        </BodyCell>
        <BodyCell sx={{ maxWidth: 80, p: 0 }}>
          {!hasChildren && (
            <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <Stack direction="row">
                <TooltipDiv tooltipMessage="Undo" place="top">
                  <span style={{ display: "inline-flex" }}>
                    <IconButton
                      className="actions-button"
                      onClick={() => onUndoSecurity(row)}
                      disabled={!row.undoable}
                    >
                      <UndoOutlinedIcon />
                    </IconButton>
                  </span>
                </TooltipDiv>
                <IconButton className="actions-button" onClick={(e) => onAssignSecurity(e, row)}>
                  <MoreVertOutlinedIcon />
                </IconButton>
              </Stack>
            </RoleComponentGuard>
          )}
        </BodyCell>
      </TableRow>
      {hasChildren &&
        isExpanded &&
        row.children &&
        row.children.map((child, index) => (
          <SecuritiesTableRow
            key={row.deal_id + "-" + child.security_id + "-" + index}
            row={child}
            depth={depth + 1}
            expanded={expanded}
            onToggle={onToggle}
            onSecurityClick={onSecurityClick}
            onAssignSecurity={onAssignSecurity}
            onUndoSecurity={onUndoSecurity}
            onAssetClassChange={onAssetClassChange}
          />
        ))}
    </>
  );
};
